import React, { useState } from "react";
import { motion } from "framer-motion";
import { BarChart3, Loader2, TrendingUp, Users, Lightbulb, AlertTriangle, ListChecks, Target, Download, FileText, ExternalLink, Activity } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const scoreColor = (s) => (s == null ? "text-sec" : s >= 66 ? "text-emerald-400" : s >= 40 ? "text-amber-400" : "text-red-400");

function TrendChart({ data }) {
  if (!data || data.length < 2) return null;
  const points = data.map((d) => ({
    date: new Date(d.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    score: d.score,
  })).filter((p) => p.score != null);
  if (points.length < 2) return null;
  return (
    <div className="rud-card rounded-2xl p-6" data-testid="sentiment-trend-chart">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-4"><Activity size={14} /> Sentiment momentum</div>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={points} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "#15151a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontSize: 12 }} labelStyle={{ color: "#9ca3af" }} itemStyle={{ color: "#FF9D00" }} />
          <Line type="monotone" dataKey="score" stroke="#FF9D00" strokeWidth={2.5} dot={{ r: 3, fill: "#FF9D00" }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function BusinessInsights() {
  const { lang, meta, countryObj, user } = useApp();
  const [f, setF] = useState({ subject: "", industry: "", region: countryObj?.name || "", question: "" });
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [out, setOut] = useState({ subject: "", scope: "", title: "", sources: [] });
  const [history, setHistory] = useState([]);
  const [exporting, setExporting] = useState("");
  const industries = meta?.industries || [];
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  const run = async () => {
    setLoading(true); setRes(null); setHistory([]);
    try {
      const { data } = await api.post("/insights/business", { ...f, region: f.region || countryObj?.name || "", lang });
      setRes(data.result);
      setOut({ subject: data.subject || "", scope: data.scope || f.region, title: data.title || "", sources: data.sources || [] });
      if (data.subject) {
        api.get("/insights/sentiment/history", { params: { subject: data.subject, scope: data.scope } })
          .then(({ data: h }) => setHistory(h.history || [])).catch(() => {});
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const exportAs = async (fmt) => {
    if (!res) return;
    setExporting(fmt);
    const b = user?.branding || {};
    try {
      const { data } = await api.post("/export", {
        title: out.title || `Sentiment & Signals — ${f.industry || "Market"}`,
        deliverable_label: out.subject ? "Sentiment Briefing" : "Sentiment & Signals",
        region: out.scope, content: res, format: fmt, site_url: window.location.origin,
        brand_company: b.company_name || "", brand_logo: b.logo || "",
        brand_name: b.display_name || user?.name || "", brand_title: b.title || "",
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a"); a.href = url; a.download = `My Kind-Sentiment-Signals.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${fmt.toUpperCase()}`);
    } catch (e) { toast.error("Export failed. Please try again."); }
    finally { setExporting(""); }
  };

  const Block = ({ icon: I, title, items }) => (items?.length ? (
    <div className="rud-card rounded-2xl p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><I size={14} /> {title}</div>
      <ul className="space-y-1.5">{items.map((x, i) => <li key={i} className="text-sm text-sec flex gap-2"><span className="text-brand mt-1">·</span>{x}</li>)}</ul>
    </div>
  ) : null);

  const score = res?.sentiment?.score ?? null;
  const label = res?.sentiment?.label ?? "";

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><BarChart3 size={14} /> Sentiment & Signals</span>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Sentiment &amp; Signals</h1>
      <p className="text-sec mt-3 max-w-2xl">Live, named sentiment and market signals on any brand, person, organisation, product or market — grounded in real-time web sources you can trust and cite.</p>

      <div className="rud-card rounded-2xl p-6 mt-8">
        <div className="mb-3">
          <label className="text-xs text-sec">Subject — a specific brand, person, org, product or topic</label>
          <input data-testid="biz-subject" value={f.subject} onChange={set("subject")} placeholder="e.g. Palantir, Narendra Modi, Tesla Cybertruck…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-1" />
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          <select data-testid="biz-industry" value={f.industry} onChange={set("industry")} className="rud-input rounded-lg px-3 py-2.5 text-sm">
            <option value="">Any industry</option>
            {industries.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
          </select>
          <div className="flex gap-1.5">
            <input data-testid="biz-region" value={f.region} onChange={set("region")} placeholder="Scope (e.g. Bharat (India), Global)" className="rud-input rounded-lg px-3 py-2.5 text-sm flex-1 min-w-0" />
            <button type="button" data-testid="biz-scope-global" onClick={() => setF((p) => ({ ...p, region: "Global" }))} className={`shrink-0 rounded-lg px-3 text-sm border transition-colors ${f.region === "Global" ? "border-brand bg-brand text-black font-semibold" : "border-rud text-sec hover:border-brand"}`}>Global</button>
          </div>
          <input data-testid="biz-question" value={f.question} onChange={set("question")} placeholder="Focus (optional)" className="rud-input rounded-lg px-3 py-2.5 text-sm" />
        </div>
        <button data-testid="biz-run" onClick={run} disabled={loading} className="btn-primary h-11 px-6 mt-3 flex items-center gap-2 disabled:opacity-60">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Analyzing signals…</> : <><TrendingUp size={16} /> {f.subject.trim() ? `Get sentiment of ${f.subject.trim()}` : "Get sentiment & signals"}</>}
        </button>
      </div>

      {res && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-4" data-testid="biz-result">
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <button data-testid="biz-export-pdf" onClick={() => exportAs("pdf")} disabled={!!exporting} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">{exporting === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF</button>
            <button data-testid="biz-export-docx" onClick={() => exportAs("docx")} disabled={!!exporting} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">{exporting === "docx" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Word</button>
            <button data-testid="biz-export-pptx" onClick={() => exportAs("pptx")} disabled={!!exporting} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">{exporting === "pptx" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} PPT</button>
          </div>

          <div className="rud-card rounded-2xl p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><Target size={14} /> {out.subject ? "Named · Live web-grounded" : "Market · Live web-grounded"}</div>
            <p className="font-serif text-2xl sm:text-3xl leading-snug" data-testid="biz-title">{out.title}</p>
            {(score != null || res.headline) && (
              <div className="mt-3 flex items-start gap-3 flex-wrap">
                {score != null && <span className={`text-2xl font-semibold tabular-nums ${scoreColor(score)}`} data-testid="biz-sentiment-score">{score}<span className="text-sm text-sec">/100</span></span>}
                {label && <span className="text-sm text-sec uppercase tracking-wide mt-1.5">{label}</span>}
              </div>
            )}
            {res.headline && <p className="text-[var(--rud-text)] mt-2" data-testid="biz-gist">{res.headline}</p>}
            {res.sentiment?.summary && res.sentiment.summary !== res.headline && <p className="text-sm text-sec mt-2">{res.sentiment.summary}</p>}
          </div>

          <TrendChart data={history} />

          <div className="grid sm:grid-cols-2 gap-4">
            <Block icon={TrendingUp} title="Demand signals" items={res.demand_signals} />
            <Block icon={Users} title="Consumer patterns" items={res.consumer_patterns} />
            <Block icon={Lightbulb} title="Opportunities" items={res.opportunities} />
            <Block icon={AlertTriangle} title="Risks" items={res.risks} />
            <Block icon={ListChecks} title="Recommended actions" items={res.recommended_actions} />
            {res.kpis?.length > 0 && (
              <div className="rud-card rounded-2xl p-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><Target size={14} /> KPIs to track</div>
                <div className="space-y-2">{res.kpis.map((k, i) => <div key={i} className="rounded-lg bg-elevated border border-rud p-2.5 flex justify-between gap-2"><span className="text-sm">{k.metric}</span><span className="text-sm text-brand">{k.target}</span></div>)}</div>
              </div>
            )}
          </div>

          {out.sources?.length > 0 && (
            <div className="rud-card rounded-2xl p-6" data-testid="biz-sources">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><ExternalLink size={14} /> Sources behind this read</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {out.sources.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer" className="block rounded-lg border border-rud bg-elevated p-3 hover:border-brand/50 transition-colors" data-testid={`biz-source-${i}`}>
                    <div className="text-sm text-[var(--rud-text)] flex items-center gap-1.5"><ExternalLink size={12} className="text-brand shrink-0" /> {s.title}</div>
                    {s.snippet && <p className="text-xs text-sec mt-1 line-clamp-2">{s.snippet}</p>}
                    <div className="text-[10px] text-sec mt-1 truncate">{s.url}{s.published ? ` · ${s.published}` : ""}</div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
